/**
 * Reading the config registry's structured errors off an http-utils rejection.
 *
 * The API deliberately returns a stable `code` alongside the message, because
 * the status alone is ambiguous — KEY_EXISTS and VERSION_CONFLICT are both 409,
 * and they call for completely different UI (rename the key vs. reload the
 * config). Branch on the code, never on the status.
 */

export interface ConfigSchemaViolation {
  /** JSON Pointer into the value; '/' is the document root. */
  path: string;
  message: string;
  /** The JSON Schema keyword that failed, e.g. 'required' or 'maximum'. */
  keyword: string;
}

export type ConfigRegistryErrorCode =
  | 'INVALID_JSON'
  | 'INVALID_SCHEMA'
  | 'SCHEMA_VALIDATION_FAILED'
  | 'NOT_FOUND'
  | 'VERSION_NOT_FOUND'
  | 'KEY_EXISTS'
  | 'VERSION_CONFLICT'
  | 'KEY_PROTECTED';

export interface ConfigRegistryErrorBody {
  success: false;
  error: string;
  code: ConfigRegistryErrorCode;
  violations?: ConfigSchemaViolation[];
  expectedVersion?: number;
  currentVersion?: number;
}

/** Null for transport failures and 500s, which carry no registry code. */
export const readConfigRegistryError = (error: unknown): ConfigRegistryErrorBody | null => {
  const body = (error as { response?: { data?: ConfigRegistryErrorBody } })?.response?.data;
  return body?.code ? body : null;
};

/**
 * A human sentence for any failure, registry-coded or not.
 *
 * VERSION_CONFLICT is reworded rather than passed through: the server's message
 * names two version numbers, which is precise but does not tell an operator what
 * to do about it.
 */
export const configRegistryErrorMessage = (error: unknown, fallback: string): string => {
  const body = readConfigRegistryError(error);

  if (body?.code === 'VERSION_CONFLICT') {
    return `Someone else saved this config while you were editing (it is now v${body.currentVersion}, you loaded v${body.expectedVersion}). Reload to see their change — your edit has not been applied.`;
  }

  if (body) return body.error;

  return (error as { message?: string })?.message || fallback;
};
